import React, { useState, useEffect, useRef } from 'react';

const AnimatedStat = ({ value, label, icon: IconComponent, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const statRef = useRef(null);

  // Extract numeric value from string like "17M+" or "170+" or "45+"
  const extractNumber = (val) => {
    const match = val.match(/[\d.]+/);
    return match ? parseFloat(match[0]) : 0;
  };

  // Get suffix like "M+", "+", etc.
  const getSuffix = (val) => {
    return val.replace(/[\d.]+/, '');
  };

  const numericValue = extractNumber(value);
  const suffix = getSuffix(value);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateCount();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (statRef.current) {
      observer.observe(statRef.current);
    }

    return () => {
      if (statRef.current) {
        observer.unobserve(statRef.current);
      }
    };
  }, [hasAnimated]);

  const animateCount = () => {
    const startTime = Date.now();
    const endValue = numericValue;

    const updateCount = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = easeOutQuart * endValue;
      
      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(updateCount);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(updateCount);
  };

  const formatValue = (val) => {
    // Format with one decimal place if needed
    if (val % 1 !== 0) {
      return val.toFixed(1);
    }
    return Math.floor(val);
  };

  return (
    <div ref={statRef} className="stat-card">
      <IconComponent className="stat-icon" size={40} />
      <div className="stat-value">
        {formatValue(count)}{suffix}
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
};

export default AnimatedStat;
